/**
 * Centralized logger for all frontend apps.
 *
 * Log levels:
 *   debug  → development only (verbose traces, per-render checks, request bodies)
 *   info   → important operational events (session start, user linked, navigation)
 *   warn   → expected error conditions (duplicates removed, fallback used, auth redirect)
 *   error  → unexpected failures (fetch errors, parse errors, exceptions)
 *
 * In production (import.meta.env.PROD === true), only warn and error emit output.
 * To enable debug in dev, set VITE_LOG_LEVEL=debug in the app .env file.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'silent';

const LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  silent: 4,
};

function resolveLevel(): LogLevel {
  // import.meta.env is available in Vite apps (both console and web-payment)
  const envLevel = (import.meta.env.VITE_LOG_LEVEL as LogLevel | undefined) ?? null;
  if (envLevel && envLevel in LEVELS) return envLevel;
  if (import.meta.env.PROD) return 'warn';
  return 'debug';
}

const currentLevel = LEVELS[resolveLevel()];

export const logger = {
  debug: (...args: unknown[]): void => {
    if (currentLevel <= LEVELS.debug) {
      console.log(...args);
    }
  },
  info: (...args: unknown[]): void => {
    if (currentLevel <= LEVELS.info) {
      console.info(...args);
    }
  },
  warn: (...args: unknown[]): void => {
    if (currentLevel <= LEVELS.warn) {
      console.warn(...args);
    }
  },
  error: (...args: unknown[]): void => {
    if (currentLevel <= LEVELS.error) {
      console.error(...args);
    }
  },
};
